import {
  FormAutosuggest,
  FormDateSuggest,
  FormPlaceSuggest,
  useFormState,
  useWatch,
  useZionFormContext,
} from '@fs/zion-form'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext, getNestedOrFloatingElementById } from '@fs/zion-sls'
import { Alert, Cell, FlowGrid, IconButton, LabelSpacer, Row, useOverlay } from '@fs/zion-ui'
import { ContentDelete } from '@fs/zion-icon'
import FieldsSection from './FieldsSection'
import ConfirmUnlinkEvent from './ConfirmUnlinkEvent'
import LinkNamesButton from '../../panels/shared/LinkNamesButton'
import { useLinkNamesSave } from '../../panels/linkNamesPanel/hooks/useLinkNamesSave'
import { createEmptyEventWithType, parseEventToSimplifiedForm } from '../utils/eventsParsing.utils'
import { hasAnyDirtyField } from '../../../../utils/formResolvers.utils'
import RelationshipEventFields from './RelationshipEventFields'
import OtherEventFields from './OtherEventFields'

const EventForm = ({
  event,
  index,
  formArrayMethods,
  onEventRemoved = () => {},
  eventAutosuggestOptions,
  person,
  splitScreenImageArk,
}) => {
  const [t] = useTranslation()
  const slsState = useSlsStateContext()
  const { trigger, getValues, control, resetField } = useZionFormContext()
  const { dirtyFields } = useFormState({ control })
  const confirmUnlinkOverlay = useOverlay()
  const [personToUnlink, setPersonToUnlink] = useState(null)

  const fieldNamePrefix = event?.primary ? 'events.primary' : 'events.additional'
  const eventTypeFieldName = `${fieldNamePrefix}[${index}].eventType`
  const basePath = `${fieldNamePrefix}[${index}]`

  // Watch for existing event ID and linkedPersonIds from form data
  const existingEventId = useWatch({ control, name: `${basePath}.existingEventId` })
  const linkedPersonIds = useWatch({ control, name: `${basePath}.linkedPersonIds` })
  const eventTypeField = useWatch({ name: eventTypeFieldName })
  const showRelationshipFields = eventTypeField?.isRelationshipEvent
  const isPersistedRelationshipEvent = event?.relationship?.id || event?.relationshipId

  // Watch the actual form values for dates, places, and other to ensure we render current state
  const watchedDates = useWatch({ name: `${basePath}.dates` }) || event.dates
  const watchedPlaces = useWatch({ name: `${basePath}.places` }) || event.places
  const watchedOther = useWatch({ name: `${basePath}.other` }) || event.other

  // Check if this is a linked event (has linkedPersonIds with more than 1 person)
  const isLinkedEvent = linkedPersonIds?.length > 1

  // Show a single generic alert once any date/place/other field on a linked additional event
  // has been edited, instead of a per-input warning message. Reuses the same dirty-field check
  // the save logic uses to decide what to persist, so the alert stays in sync with actual changes.
  // (eventType dirtiness is intentionally excluded.) Primary events surface their own always-on alert.
  const hasDirtyEventFields = ['dates', 'places', 'other'].some((section) =>
    hasAnyDirtyField(dirtyFields, `${basePath}.${section}`)
  )
  const showLinkedChangeAlert = !event?.primary && isLinkedEvent && hasDirtyEventFields

  // Derive the selected event based on existingEventId
  const selectedEvent = useMemo(() => {
    if (!existingEventId) return null

    // Try to find the event in the SLS state
    return getNestedOrFloatingElementById(slsState.images, existingEventId)
  }, [existingEventId, slsState.images])

  // Use the hook for handling link names save
  const { handleLinkNamesSave } = useLinkNamesSave({
    getValues,
    images: slsState.images,
    currentImageArk: slsState.currentImageArk,
    updateField: formArrayMethods.update,
    basePath: fieldNamePrefix,
  })

  // Callback to trigger validation on both date and place fields when either field blurs
  const handleValidateEventFields = useCallback(async () => {
    // Build paths for all date and place sub-element fields in THIS event
    const datePaths = (watchedDates || []).map(
      (dateItem, dateIndex) => `${fieldNamePrefix}[${index}].dates[${dateIndex}].value`
    )
    const placePaths = (watchedPlaces || []).map(
      (placeItem, placeIndex) => `${fieldNamePrefix}[${index}].places[${placeIndex}].value`
    )

    // Trigger validation on all date and place fields
    // This ensures errors appear on both fields when both are empty
    const allPaths = [...datePaths, ...placePaths]
    if (allPaths.length > 0) {
      await trigger(allPaths, { shouldFocus: false })
    }
  }, [fieldNamePrefix, index, watchedDates, watchedPlaces, trigger])

  // Records the event's current form value (including live linkedPersonIds after any panel
  // link/unlink) into events.removed, then removes it from its live array. Capturing before the
  // remove is what lets save-time deletion reconcile pending unlinks against SLS instead of losing
  // them. getValues gives the persisted form value; linkedPersonIds is overlaid from the watched
  // value so an in-session unlink made via updateField is reflected.
  const handleRemoveEvent = useCallback(() => {
    const currentEventValue = getValues(basePath) || event
    onEventRemoved({ ...currentEventValue, linkedPersonIds: linkedPersonIds ?? currentEventValue?.linkedPersonIds })
    formArrayMethods.remove(index)
    trigger() // Re-run validation after removal
  }, [getValues, basePath, event, onEventRemoved, linkedPersonIds, formArrayMethods, index, trigger])

  return (
    <>
      <FlowGrid
        orientation="vertical"
        columnCount={1}
        gutters="nano"
        key={`event-${event.id}-${existingEventId || 'new'}`}
      >
        <Cell>
          <FlowGrid columnCount={1} gutters="xxs">
            {/* Event type selector and link-names button share a Cell so no gutter is added
                between them; the FlowGrid gutter only separates this group from the alert. */}
            <Cell>
              <Row gutters="xxs" alignItemsY="middle">
                {/* Event type autosuggest */}
                <FormAutosuggest
                  name={eventTypeFieldName}
                  label={t('edit-events.fields.event-type', 'Event Type')}
                  data-testid="event-type-autosuggest"
                  suggestions={eventAutosuggestOptions}
                  onSuggestionSelected={(selectedSuggestion) => {
                    // Check if an existing event was selected
                    if (selectedSuggestion?.isExistingEvent && selectedSuggestion?.existingEvent) {
                      const existingEvent = selectedSuggestion.existingEvent

                      // Populate form values from the existing event
                      const newEventData = parseEventToSimplifiedForm(
                        existingEvent,
                        slsState.types,
                        person,
                        slsState.images
                      )

                      const newEventDataWithUpdatedLinkedPersonIds = {
                        ...newEventData,
                        primary: event.primary, // Preserve whether this is primary or additional
                        added: true, // Mark as added to process it as a new event, we do more checks on save to link this event
                        shared: true, // Mark as shared to indicate it has multiple linked persons
                      }

                      // Reset field with new event data. This both updates the form values and clears
                      // dirty/touched state to prevent warning messages from persisting between event switches.
                      formArrayMethods.update(index, newEventDataWithUpdatedLinkedPersonIds)
                      resetField(basePath, { defaultValue: newEventDataWithUpdatedLinkedPersonIds })
                    } else {
                      // Standard event type selected (new event)
                      // Start with a clean empty event structure to avoid ghost fields from previous selections
                      const emptyEventData = createEmptyEventWithType(
                        selectedSuggestion?.key,
                        slsState.types,
                        person,
                        event.primary
                      )

                      // Reset field with empty event structure. This both updates the form values and
                      // clears dirty/touched state to prevent warning messages from persisting.
                      formArrayMethods.update(index, emptyEventData)
                      resetField(basePath, { defaultValue: emptyEventData })
                    }
                  }}
                  clearOnClickAway
                  clearable
                  growX
                  dense
                  // Disable to prevent editing of persisted relationship events. We'll look at a better UX later.
                  // This is current behavior from RII.
                  disabled={isPersistedRelationshipEvent}
                  // Virtualization is disabled due to issues with grouped dropdown rendering.
                />
                {!event.primary && (
                  <Cell>
                    <LabelSpacer />
                    <IconButton
                      Icon={ContentDelete}
                      title={t('edit-events.remove-event', 'Remove Event')}
                      onClick={() => {
                        if (isLinkedEvent) {
                          setPersonToUnlink(person)
                          confirmUnlinkOverlay.open()
                          return
                        }
                        handleRemoveEvent()
                      }}
                      color="danger"
                      data-testid={`unlink-event-button-${index}`}
                      linkName="Events Card Unlink Event"
                    />
                  </Cell>
                )}
              </Row>
              {/* Show View Linked Names button for events with multiple linked persons */}
              {event?.shared && (
                <LinkNamesButton
                  label={t('edit-events.view-linked-names', 'View linked names to event')}
                  person={person}
                  field={event}
                  onSave={handleLinkNamesSave(index)}
                  linkedPersonIds={linkedPersonIds}
                  description={t(
                    'shared-event.panel-description',
                    'Do more names need to be linked to this {eventType} event?',
                    {
                      eventType: event?.eventType?.primaryText,
                    }
                  )}
                  preselectCurrentPerson
                />
              )}
            </Cell>
            {/* Generic alert shown once a linked additional event field changes, replacing the
                per-input warning messages. Rendered below the link-names button. */}
            {showLinkedChangeAlert && (
              <Cell>
                <Alert type="warning" outlined dense>
                  {t('edit-events.linked-field-change-warning', 'Changes will be made on all who are linked.')}
                </Alert>
              </Cell>
            )}
          </FlowGrid>
        </Cell>

        {/* Date Fields */}
        <FieldsSection
          path={`${basePath}.dates`}
          indexName="dates"
          eventIndex={index}
          SuggestComponent={FormDateSuggest}
          suggestFieldType="DATE"
          suggestLabelKey="edit-events.fields.date"
          suggestDefaultLabel="Date"
          primaryEvent={event.primary}
          splitScreenImageArk={splitScreenImageArk}
          onValidateFields={handleValidateEventFields}
        />

        {/* Place Fields */}
        <FieldsSection
          path={`${basePath}.places`}
          indexName="places"
          eventIndex={index}
          SuggestComponent={FormPlaceSuggest}
          suggestFieldType="PLACE"
          suggestLabelKey="edit-events.fields.place"
          suggestDefaultLabel="Place"
          primaryEvent={event.primary}
          splitScreenImageArk={splitScreenImageArk}
          onValidateFields={handleValidateEventFields}
        />

        {watchedOther?.map((other, otherIndex) => (
          <OtherEventFields
            key={`other-${other.id}`}
            otherField={other}
            itemIndex={otherIndex}
            eventIndex={index}
            primaryEvent={event?.primary}
            splitScreenImageArk={splitScreenImageArk}
          />
        ))}

        {showRelationshipFields && (
          <RelationshipEventFields person={person} index={index} fieldNamePrefix={fieldNamePrefix} />
        )}
      </FlowGrid>
      {/* Confirm Unlink Dialog */}
      <ConfirmUnlinkEvent
        overlay={confirmUnlinkOverlay}
        person={personToUnlink}
        event={selectedEvent}
        onRemove={() => {
          if (!personToUnlink || !selectedEvent) return

          // Close dialog immediately (consistent with ConfirmUnlinkName pattern)
          confirmUnlinkOverlay.close()

          // Remove the event from the form so it disappears from this person's card. The save-time
          // dispatch logic (handleDeletedEvents) decides the backend effect: if the event is shared
          // with other persons it only unlinks the focus person (REM_SUB); otherwise it deletes the
          // event entirely.
          handleRemoveEvent()
          setPersonToUnlink(null)
        }}
      />
    </>
  )
}

export default EventForm
